import { Button } from '../../../../base/Button/Button';
import { useState } from 'preact/hooks';
import { AdvancedJourneyOptions } from '../AdvancedOptions/AdvancedJourneyOptions';
import { JourneyTimeSelector } from '../TimeSelector/JourneyTimeSelector';
import { LocationInputs } from '../LocationInputs/LocationInputs';
import { createDefaultJourneyPlanAdvancedFilters } from '../../../../../data/journeyPlanSearch';
import { submitJourneyPlanForm } from './JourneyPlanForm.types';
import type { JourneyPlanFormModel } from './JourneyPlanForm.types';
import styles from './JourneyPlanForm.module.css';
import { PagePadding } from '../../../../widget/Pages/Pages';

interface JourneyPlanFormProps {
  form: JourneyPlanFormModel;
  mode?: 'search' | 'results';
  timingLabel?: string;
  onSubmit?: (event: Event) => void;
  onAdvancedJourneyOptionsToggle?: (isOpen: boolean) => void;
}

export function JourneyPlanForm({
  form,
  mode = 'search',
  timingLabel,
  onSubmit,
  onAdvancedJourneyOptionsToggle,
}: JourneyPlanFormProps) {
  const [isEditingResults, setIsEditingResults] = useState(false);
  const [isAdvancedOptionsOpen, setIsAdvancedOptionsOpen] = useState(false);
  const isResultsMode = mode === 'results';
  const showResultsSummary = isResultsMode && !isEditingResults;
  const baseSubmit =
    onSubmit ??
    (isResultsMode
      ? submitJourneyPlanForm(form, () => setIsEditingResults(false))
      : form.submit.handleSubmit);
  const closeAdvancedJourneyOptions = () => {
    setIsAdvancedOptionsOpen(false);
    onAdvancedJourneyOptionsToggle?.(false);
  };
  const handleSubmit = (event: Event) => {
    closeAdvancedJourneyOptions();
    return baseSubmit(event);
  };
  const handleAdvancedJourneyOptionsToggle = (isOpen: boolean) => {
    setIsAdvancedOptionsOpen(isOpen);
    onAdvancedJourneyOptionsToggle?.(isOpen);
  };
  const advancedJourneyOptions = (
    <PagePadding>
      <AdvancedJourneyOptions
        filters={form.filters.value}
        onFiltersChange={form.filters.onChange}
        open={isAdvancedOptionsOpen}
        onToggle={handleAdvancedJourneyOptionsToggle}
      />
    </PagePadding>
  );

  const handleResetAdvancedFilters = () => {
    form.filters.onChange(createDefaultJourneyPlanAdvancedFilters());
  };

  if (showResultsSummary) {
    return (
      <div className={styles.resultsSummary}>
        <div className={styles.summaryHeader}>
          <strong>
            {form.locations.originQuery} to {form.locations.destinationQuery}
          </strong>
          {timingLabel && (
            <span className={styles.leavingTime}>{timingLabel}</span>
          )}
          <Button
            className={styles.changeJourneyButton}
            onClick={() => setIsEditingResults(true)}
            variant="link"
          >
            Change journey
          </Button>
        </div>
        {advancedJourneyOptions}
        {isAdvancedOptionsOpen && (
          <div className={styles.advancedOptionsActions}>
            <Button
              variant="secondary"
              fullWidth={true}
              onClick={handleResetAdvancedFilters}
            >
              Reset filters
            </Button>
            <Button variant="primary" fullWidth={true} onClick={handleSubmit}>
              Show results
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <LocationInputs
        startValue={form.locations.originQuery}
        destinationValue={form.locations.destinationQuery}
        onStartChange={form.locations.onOriginChange}
        onDestinationChange={form.locations.onDestinationChange}
        onStartSelectOption={form.locations.onOriginSelectOption}
        onDestinationSelectOption={form.locations.onDestinationSelectOption}
        onSwap={form.locations.onSwapLocations}
        onUseStartCurrentLocation={form.locations.onUseCurrentOrigin}
        onUseDestinationCurrentLocation={form.locations.onUseCurrentDestination}
        startOptions={form.locations.originOptions}
        destinationOptions={form.locations.destinationOptions}
        disableStartCurrentLocation={
          form.locations.destinationIsCurrentLocation
        }
        disableDestinationCurrentLocation={
          form.locations.originIsCurrentLocation
        }
      />

      <JourneyTimeSelector
        mode={form.time.mode}
        dateValue={form.time.date}
        timeValue={form.time.time}
        onModeChange={form.time.onModeChange}
        onDateChange={form.time.onDateChange}
        onTimeChange={form.time.onTimeChange}
      />

      {advancedJourneyOptions}

      <div className={styles.actions}>
        <Button fullWidth={true} type="submit" loading={form.submit.loading}>
          Get directions
        </Button>
      </div>
    </form>
  );
}
